import { ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Home, Wallet, BarChart3, Clock, User, HelpCircle, LogOut, Smartphone } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppApiBalance, formatAudBalance } from "@/hooks/useAppApiBalance";
import { AppStoreBadgesOfficial } from "@/components/connect/AppStoreBadgesOfficial";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import elefaLogo from "@/assets/elefa-app-icon.webp";

/**
 * AuthedWebShell — Rebtel-style wallet-first chrome for the authenticated
 * web experience (spec §7/§8, prototype v1 "Wallet-first hub").
 *
 * Structure:
 *   - Desktop ≥md: 260px left rail (Home / Wallet / Rates / History /
 *     Referrals / Support · Account + Sign out pinned bottom).
 *     Top header with page title + balance chip + avatar.
 *   - Mobile <md: no rail; sticky bottom tabs Home / Wallet / Rates / Account.
 *   - Every authed web page gets the same dark "Ready to talk?" strip
 *     with App Store + Google Play badges (calling lives in the app).
 *
 * Native iOS/Android is NOT affected — ConnectPortalLayout only mounts
 * this shell when !isNative && user is signed in.
 */

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
}

const RAIL_ITEMS: NavItem[] = [
  { to: "/connect-portal/dashboard", label: "Home", icon: Home },
  { to: "/connect-portal/topup",     label: "Wallet", icon: Wallet },
  { to: "/connect-portal/rates",     label: "Rates", icon: BarChart3 },
  { to: "/connect-portal/history",   label: "History", icon: Clock },
];

const TAB_ITEMS: NavItem[] = [
  { to: "/connect-portal/dashboard", label: "Home", icon: Home },
  { to: "/connect-portal/topup",     label: "Wallet", icon: Wallet },
  { to: "/connect-portal/rates",     label: "Rates", icon: BarChart3 },
  { to: "/connect-portal/me",        label: "Account", icon: User },
];

const PAGE_TITLES: Record<string, string> = {
  "/connect-portal/dashboard": "Home",
  "/connect-portal/topup":     "Top up",
  "/connect-portal/rates":     "Rates",
  "/connect-portal/history":   "Call history",
  "/connect-portal/me":        "Account",
  "/connect-portal/help":      "Support",
  "/connect-portal/legal":     "Legal",
};

function initialsFor(email: string | null | undefined, name: string | null | undefined): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] ?? "").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "EL";
}

export function AuthedWebShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { balanceAud, isLoading: balanceLoading } = useAppApiBalance();
  const { user } = useAuth();

  const pageTitle = PAGE_TITLES[location.pathname] ?? "elefa";
  const initials = initialsFor(user?.email ?? null, (user?.user_metadata as any)?.full_name ?? null);

  return (
    <div className="min-h-[100dvh] bg-[hsl(var(--muted))]/40 text-foreground">
      <div className="mx-auto flex min-h-[100dvh] max-w-[1400px]">
        {/* Desktop left rail */}
        <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-border/60 bg-card/60 backdrop-blur-sm px-6 py-8">
          <Link
            to="/connect-portal/dashboard"
            className="mb-10 flex items-center gap-2.5 group"
            aria-label="elefa home"
          >
            <img
              src={elefaLogo}
              alt=""
              className="h-9 w-9 rounded-xl shadow-sm ring-1 ring-border/60 transition-transform group-hover:scale-105"
            />
            <span className="text-2xl font-bold tracking-tight text-primary leading-none">
              elefa
            </span>
          </Link>

          <nav className="flex-1 space-y-1">
            {RAIL_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/connect-portal/dashboard"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-background text-primary shadow-sm border border-border/70"
                      : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
                  )
                }
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto space-y-1 border-t border-border/60 pt-6">
            <NavLink
              to="/connect-portal/me"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-background text-primary shadow-sm border border-border/70"
                    : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
                )
              }
            >
              <User className="h-5 w-5" aria-hidden="true" />
              Account
            </NavLink>
            <NavLink
              to="/connect-portal/help"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )
              }
            >
              <HelpCircle className="h-5 w-5" aria-hidden="true" />
              Support
            </NavLink>
            <button
              type="button"
              onClick={() => { supabase.auth.signOut(); }}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <LogOut className="h-5 w-5" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main pane */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top header */}
          <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-border/60 bg-background/85 px-4 backdrop-blur md:px-8">
            <div className="flex items-center gap-3">
              <Link
                to="/connect-portal/dashboard"
                className="md:hidden flex items-center gap-2"
                aria-label="elefa home"
              >
                <img src={elefaLogo} alt="" className="h-7 w-7 rounded-lg ring-1 ring-border/60" />
                <span className="text-xl font-bold tracking-tight text-primary leading-none">elefa</span>
              </Link>
              <h1 className="hidden md:block text-lg font-semibold text-foreground">{pageTitle}</h1>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/connect-portal/topup"
                className="hidden sm:flex flex-col items-end leading-tight hover:opacity-80 transition-opacity"
                aria-label="Top up wallet"
              >
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Balance
                </span>
                <span className="text-sm font-bold text-foreground tabular-nums">
                  {balanceLoading || balanceAud == null ? "Not available" : formatAudBalance(balanceAud)}
                </span>
              </Link>
              <Link
                to="/connect-portal/me"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-bold text-muted-foreground ring-1 ring-border/70 hover:text-foreground transition-colors"
                aria-label="Account"
              >
                {initials}
              </Link>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 px-4 pb-32 pt-6 md:px-8 md:pb-16 md:pt-8">
            <div className="mx-auto w-full max-w-4xl space-y-8">
              {children}

              {/* Get-the-app strip — spec §7 */}
              <section className="relative flex flex-col items-center overflow-hidden rounded-3xl bg-[hsl(var(--elefa-dark))] px-6 py-8 text-center text-primary-foreground md:px-8">
                <div className="mx-auto flex w-full max-w-md flex-col items-center text-center">
                  <h2 className="mb-2 w-full text-center text-2xl font-bold">
                    Ready to talk?
                  </h2>
                  <p className="mx-auto mb-6 max-w-sm text-center text-primary-foreground/70">
                    Calling lives in the app. Your account and balance are already here. Just download and dial.
                  </p>
                  <AppStoreBadgesOfficial className="justify-center" />
                </div>
              </section>

              <footer className="pb-2 text-center text-xs text-muted-foreground">
                Calls are charged per minute (rounded up after 60s) · credit never expires · elefa Mobile Pty Ltd · ABN 16 694 346 088
              </footer>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-border/60 bg-background/95 px-2 py-2 backdrop-blur md:hidden"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.5rem)" }}
      >
        {TAB_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/connect-portal/dashboard"}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center gap-1 rounded-lg py-2 text-[10px] font-bold uppercase tracking-wider transition-colors",
                isActive ? "text-primary" : "text-muted-foreground",
              )
            }
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Small helper for pages to render a "web only" branch without importing
 * useIsNative individually. Keeps native rendering untouched.
 */
export { Smartphone };
